import i18n from "../../i18n";

// Supervisor UI languages. Autonyms live in the switcher; these are the i18n ids
// and the region codes the backend LanguagePreference API expects.
const SUPPORTED_LANGUAGE_IDS = ["en", "fr", "es"];

/**
 * Map a saved `languagePreference` region code (e.g. "es-ES") to an i18n id
 * ("es"). Falls back to English for missing / unrecognized codes.
 */
export const preferenceToLanguageId = (pref?: string | null): string => {
	if (!pref) return "en";
	const code = pref.split("-")[0].toLowerCase();
	return SUPPORTED_LANGUAGE_IDS.includes(code) ? code : "en";
};

/**
 * Map an i18n id ("es") to the region code the LanguagePreference API expects
 * ("es-ES").
 */
export const languageIdToPreference = (id: string): string => {
	switch (id) {
		case "fr":
			return "fr-FR";
		case "es":
			return "es-ES";
		default:
			return "en-US";
	}
};

/**
 * Options for UIs that configure a site's *default* language (e.g. the
 * admin Add/Edit Location form). `value` is the region code the Locations
 * Create/Edit API accepts ("en-US" | "es-ES" | "fr-FR"); labels are the
 * system's supported languages.
 */
export const SUPPORTED_SITE_LANGUAGES = [
	{ label: "English", value: "en-US" },
	{ label: "Spanish", value: "es-ES" },
	{ label: "French", value: "fr-FR" },
];

/**
 * Site language used when a Create request omits `defaultLanguage` or
 * provides an unrecognized code.
 */
export const DEFAULT_SITE_LANGUAGE = "en-US";

/**
 * Normalize a site's stored `defaultLanguage` to one of the supported region
 * codes. Missing or unrecognized codes fall back to English, mirroring how
 * the backend treats them.
 */
export const normalizeSiteLanguage = (code?: string | null): string =>
	languageIdToPreference(preferenceToLanguageId(code));

// Seed the supervisor UI language from the auth/refresh state exactly once per
// page load. Kept to once so re-decoding the (still valid, possibly stale) token
// on every protected-route navigation can't revert a change the user just made
// in the switcher.
let applied = false;

/**
 * Resolve and apply the supervisor UI language on login / bootstrap.
 *
 *   flag off                       → English (ignores any stale stored value)
 *   flag on, stored choice exists  → keep it (the live user selection)
 *   flag on, no stored choice      → the saved languagePreference, else English
 */
export const applySupervisorLanguageOnce = (
	multilingualEnabled: boolean,
	languagePreference?: string | null
): void => {
	if (applied) return;
	applied = true;

	let id = "en";
	if (multilingualEnabled) {
		const stored = localStorage.getItem("supervisor-language");
		id =
			stored && SUPPORTED_LANGUAGE_IDS.includes(stored)
				? stored
				: preferenceToLanguageId(languagePreference);
	}
	if (i18n.language !== id) i18n.changeLanguage(id);
	localStorage.setItem("supervisor-language", id);
};
